import React, { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import t from '../../../translations/main'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'

import Popup from 'src/components/Atoms/Popup/Popup'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'

import { ApiData, FormWrapperProps } from './types'
import utils from 'src/components/Organisms/Form/utils'
import { OnSubmitParams } from 'src/components/Core/Hooks/useForm/useForm'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'
import storage from 'src/components/Core/Utils/storage'

const FRONT_MOCK: boolean = (import.meta.env.VITE_MOCK_DATA || 'false') === 'true'

export const parseJson = (valueToTest: string | number | boolean | undefined | null | Record<string,string> | Record<string,string>[]) => {
  if(typeof valueToTest !== 'string') return false
  try {
    return JSON.parse(valueToTest)
  } catch (error) {
    return false
  }
}

const FormWrapper: FC<FormWrapperProps> = props => {
  const {
    flowConfig,
    currentStep,
    applicationId,
    applicationType,
    optionsList
  } = props
  const [fetchedData, setFetchedData] = useState<Record<string, Record<string, string | number>>>({})
  const [serverValidationError, setServerValidationError] = useState('')
  const [submittedSuccesifully, setSubmittedSuccesifully] = useState(false)
  const [genericError, setGenericError] = useState({error: false, message: ''})

  const { isLoading, error, sendRequest } = useFetch()
  const navigate = useNavigate()

  const formType = flowConfig?.[`${currentStep}`]?.type

  useEffect(() => {
    if (!applicationId) {
      navigate('/error/internalProblem')
    }

    const localApplicationId = Object.values(fetchedData).find(item => item.applicationId)?.applicationId
    const hasApplicationIdChanged = localApplicationId !== undefined && localApplicationId !== applicationId
    // GET
    const fetchUrl = flowConfig?.[`${currentStep}`]?.fetchUrl

    const setFetchedDataHandler = (data?: ApiData) => {
      // if(!hasApplicationIdChanged && fetchedData[`${currentStep}`]) return
      const dataWithFetchUrl = fetchUrl ? data : {}
      const newData = hasApplicationIdChanged ? { [`${currentStep}`]: { ...dataWithFetchUrl } } : { ...fetchedData,  [`${currentStep}`]: { ...dataWithFetchUrl } }
      setFetchedData(newData)
    }

    if (fetchUrl
    // && (hasApplicationIdChanged 
    // || !fetchedData[`${currentStep}`] )
    ){
      sendRequest(
        {
          scope: 'onboarding',
          url: `${fetchUrl}${FRONT_MOCK ? '' : '?applicationId=' + applicationId}`,
          method: 'GET',
        },
        setFetchedDataHandler
      )
    } else {
      setFetchedDataHandler()
    }
  }, [currentStep, applicationId])

  const submitHandler = ({ data, options, configData, setConfigData }: OnSubmitParams) => {
    if(optionsList[`${currentStep}`].state === 'complete' && optionsList.some(step => step.state === 'warning')) {
      setGenericError({error: true, message: t('onboarding.submitNotAllowed')})
      return
    }
    // eslint-disable-next-line
    const modifyResponse = (responseData?: ApiData, response?: Response) => {
      const serverValidationErrorsParsed = parseJson(responseData?.reasonPhrase)
      if(serverValidationErrorsParsed && serverValidationErrorsParsed[0].key === 'generalError') {
        setServerValidationError(t(`onboarding.errors.${serverValidationErrorsParsed[0].errorcode}`) || serverValidationErrorsParsed[0].errordescription?.split(':')[1] || t('error.invalidValue'))
        return
      }
      if(Array.isArray(serverValidationErrorsParsed) && serverValidationErrorsParsed.length > 0) {
        setConfigData(utils.injectErrorPropToConfig(configData, [], serverValidationErrorsParsed))
        return
      }

      setFetchedData(prev => ({...prev,  [`${currentStep}`]: { applicationId, ...data } }))

      if(props.isLastForm){
        const modifyResponseForSubmit = (responseData?: ApiData) => {
          const serverValidationErrorsParsed = parseJson(responseData?.reasonPhrase)
          if(Array.isArray(serverValidationErrorsParsed) && serverValidationErrorsParsed.length > 0){
            // temp message until getting the error codes from VP
            setServerValidationError('Error on submit')
          } else {
            storage.set(storage?.keys?.onboarding?.applicationStatus,'submitted')
            setSubmittedSuccesifully(true)
            props.nextStep(true)
          }
        }
        sendRequest(
          {
            scope: 'onboarding',
            url: import.meta.env.VITE_MOCK_DATA === 'true'
              ? `${window.location.origin}/mock/postMessage.json`
              : `${getBaseApiHost()}/${applicationType}/submit${applicationType}Application?applicationid=${applicationId}&step=${currentStep}`,
            method: 'POST',
            body: { },
          },
          modifyResponseForSubmit
        )
      } else if (!error) {
        props.nextStep()
      }
    }

    // POST
    const postUrl = flowConfig[`${currentStep}`].postUrl
    if (postUrl) {
      sendRequest(
        {
          scope: 'onboarding',
          url: postUrl,
          method: 'POST',
          body: { applicationId, ...data },
          headers: {
            'Content-Type':  options?.contentType || 'application/json',
          },
        },
        modifyResponse
      )
    } else {
      modifyResponse()
    }
  }

  const goBack = () => {
    const { prevStep } = props
    if (prevStep) prevStep()
  }

  if (isLoading || !fetchedData?.[`${currentStep}`]) return <Spinner removeFullscreen />

  const FormComponent = flowConfig?.[`${currentStep}`]?.form || null
  storage.set(storage?.keys?.onboarding?.stepCompleted, optionsList[`${currentStep}`]?.state)
  return (
    <>
      {
        FormComponent && (
          <FormComponent
            fetchedData={fetchedData?.[`${currentStep}`]}
            disableNext={currentStep === flowConfig.length - 1}
            disablePrev={currentStep === 0}
            nextStep={props.nextStep}
            prevStep={goBack}
            submit={submitHandler}
            isLastForm={props.isLastForm}
            setCompletedStepList={props.setCompletedStepList}
            setOptionsList={props.setOptionsList}
            formType={formType}
            setSubmittedSuccesifully={setSubmittedSuccesifully}
          />
        )
      }
      {!isLoading && (error || serverValidationError) && (
        <Popup type='error' label={error || serverValidationError} />
      )}
      {genericError.error && (
        <Popup
          removeSelfClose
          type='error'
          label={genericError.message}
          popupCloseHelperFunction={() => setGenericError({error: false, message: ''})}
          click={() => setGenericError({error: false, message: ''})}
        />
      )}
      {submittedSuccesifully && (
        <Popup
          removeSelfClose
          type='success'
          title={t('onboarding.success.title')}
          label={t('onboarding.success.label')}
          popupCloseHelperFunction={() => setSubmittedSuccesifully(false)}
          click={() => setSubmittedSuccesifully(false)}
        />
      )}
    </>
  )
}

export default FormWrapper
